import * as React from "react";
import { useRef, useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../../lib/utils";
import { ButtonSquare } from "../../atoms/ButtonSquare";
import { Avatar } from "../../atoms/Avatar";
import { PfizerLogo } from "../../atoms/icons/sidebar";

export interface SidebarNavItem {
  /** Unique identifier for the nav item */
  id: string;
  /** Icon component to display */
  icon: React.ReactNode;
  /** Accessible label for the nav item */
  label: string;
  /** Navigation URL (optional if onClick is provided) */
  href?: string;
  /** Click handler (optional if href is provided) */
  onClick?: () => void;
  /** Badge text to display (e.g., "BETA") */
  badge?: string;
}

export interface SidebarProps extends React.HTMLAttributes<HTMLElement> {
  /** Custom logo component (defaults to PfizerLogo) */
  logo?: React.ReactNode;
  /** Primary navigation items displayed at the top */
  topNavItems: SidebarNavItem[];
  /** Secondary navigation items displayed at the bottom (above user section) */
  bottomNavItems?: SidebarNavItem[];
  /** User initials for the avatar */
  userInitials: string;
  /** Full user name for the profile menu */
  userName?: string;
  /** Version name to display in profile menu (e.g., "R14.0") */
  versionName?: string;
  /** Version date to display in profile menu (e.g., "12 Feb 2026") */
  versionDate?: string;
  /** Currently active navigation item ID */
  activeItemId?: string;
  /** Handler for Intercom button click */
  onIntercomClick?: () => void;
  /** Whether to show the Intercom button */
  showIntercom?: boolean;
}

/**
 * Sidebar - Main application navigation sidebar
 *
 * A fixed-width (80px) vertical sidebar with:
 * - Logo at the top
 * - Primary navigation icons
 * - Secondary navigation icons (optional)
 * - User avatar
 * - Intercom support button (optional)
 *
 * Responsive behavior:
 * - Maintains 80px width on all screens
 * - Adjusts spacing on shorter viewport heights (max-height: 600px, 500px)
 * - Scrollable when content exceeds viewport
 */
function Sidebar({
  logo,
  topNavItems,
  bottomNavItems = [],
  userInitials,
  userName,
  versionName,
  versionDate,
  activeItemId,
  showIntercom = true,
  className,
  ...props
}: Readonly<SidebarProps>) {
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const avatarRef = useRef<HTMLButtonElement>(null);

  const handleNavClick = (item: SidebarNavItem) => {
    if (item.onClick) {
      item.onClick();
    } else if (item.href) {
      globalThis.location.href = item.href;
    }
  };

  const toggleProfileMenu = useCallback(() => {
    if (!isProfileMenuOpen && avatarRef.current) {
      const rect = avatarRef.current.getBoundingClientRect();
      // Position menu to the right of the avatar, aligned at bottom
      setMenuPosition({
        top: rect.bottom - 140, // Adjust to align menu bottom with avatar
        left: rect.right + 20, // 16px gap from avatar
      });
    }
    setIsProfileMenuOpen((prev) => !prev);
  }, [isProfileMenuOpen]);

  const closeProfileMenu = useCallback(() => {
    setIsProfileMenuOpen(false);
  }, []);

  // Handle click outside to close menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(event.target as Node) &&
        avatarRef.current &&
        !avatarRef.current.contains(event.target as Node)
      ) {
        closeProfileMenu();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeProfileMenu();
      }
    };

    if (isProfileMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isProfileMenuOpen, closeProfileMenu]);

  const renderNavItem = (item: SidebarNavItem) => {
    const isActive = activeItemId === item.id;

    return (
      <div key={item.id} className="relative flex flex-col items-center gap-0">
        <ButtonSquare
          size="large"
          icon={item.icon}
          tooltip={item.label}
          tooltipSide="right"
          isActive={isActive}
          onClick={() => handleNavClick(item)}
          aria-current={isActive ? "page" : undefined}
        />
        {item.badge && (
          <span
            className={cn(
              // Base badge styles
              "flex h-[18px] items-center gap-1",
              "rounded-md border border-stroke-secondary bg-bg-hover",
              "px-1 text-[10px] font-bold leading-[18px] text-text-primary",
              // Negative margin to close gap with icon above
              "-mt-3",
              // Responsive: smaller on short screens
              "-mt-3",
              "[@media(max-height:500px)]:h-[14px] [@media(max-height:500px)]:px-0.5",
              "[@media(max-height:500px)]:text-[8px] [@media(max-height:500px)]:leading-[14px]",
            )}
            style={{
              border: "1px solid #d4d4d8",
            }}
          >
            {item.badge}
          </span>
        )}
      </div>
    );
  };

  return (
    <nav
      className={cn(
        // Base layout
        "flex h-full w-20 min-w-[80px] max-w-[80px] shrink-0 flex-col items-center justify-between",
        // Styling
        "border-r border-stroke-secondary bg-bg-secondary",
        // Base spacing
        "py-6",
        // Responsive spacing for shorter screens
        "[@media(max-height:600px)]:py-4",
        "[@media(max-height:500px)]:py-3",
        // Scrolling
        "overflow-y-auto overflow-x-hidden",
        // Hide scrollbar
        "[&::-webkit-scrollbar]:w-0 [&::-webkit-scrollbar]:bg-transparent",
        className,
      )}
      aria-label="Main navigation"
      style={{
        borderRight: "1px solid #d4d4d8",
      }}
      {...props}
    >
      {/* Top Section: Logo + Primary Nav */}
      <div
        className={cn(
          "flex w-full shrink-0 flex-col items-center",
          // Base gap
          "gap-8",
          // Responsive gap
          "[@media(max-height:600px)]:gap-6",
          "[@media(max-height:500px)]:gap-4",
        )}
      >
        {/* Logo */}
        <div className="flex shrink-0 items-center justify-center">
          {logo || <PfizerLogo size={28} />}
        </div>

        {/* Primary Navigation */}
        <div
          className={cn(
            "flex flex-col items-center",
            // Base gap
            "gap-3",
            // Responsive gap
            "[@media(max-height:600px)]:gap-2",
            "[@media(max-height:500px)]:gap-1",
          )}
        >
          {topNavItems.map(renderNavItem)}
        </div>
      </div>

      {/* Bottom Section: Secondary Nav */}
      {bottomNavItems.length > 0 && (
        <div
          className={cn(
            "flex min-h-min w-full flex-1 flex-col items-center justify-end",
            // Base spacing
            "gap-3 py-6",
            // Responsive spacing
            "[@media(max-height:600px)]:gap-2 [@media(max-height:600px)]:py-4",
            "[@media(max-height:500px)]:gap-1 [@media(max-height:500px)]:py-3",
          )}
        >
          <div
            className={cn(
              "flex flex-col items-center",
              // Base gap
              "gap-3",
              // Responsive gap
              "[@media(max-height:600px)]:gap-2",
              "[@media(max-height:500px)]:gap-1",
            )}
          >
            {bottomNavItems.map(renderNavItem)}
          </div>
        </div>
      )}

      {/* User Section: Divider + Avatar + Intercom */}
      <div
        className={cn(
          "flex w-20 shrink-0 flex-col items-center",
          // Base spacing
          "gap-5 pb-6",
          // Responsive spacing
          "[@media(max-height:600px)]:gap-3 [@media(max-height:600px)]:pb-4",
          "[@media(max-height:500px)]:gap-2 [@media(max-height:500px)]:pb-3",
        )}
      >
        {/* Divider */}
        <div className="h-px w-full bg-bg-grey" />

        {/* User Avatar with Profile Menu */}
        <div className="relative">
          <button
            ref={avatarRef}
            type="button"
            onClick={toggleProfileMenu}
            aria-haspopup="menu"
            aria-expanded={isProfileMenuOpen}
            aria-controls="sidebar-profile-menu"
            className="cursor-pointer rounded-full border-0 bg-transparent p-0 focus:outline-none focus:ring-2 focus:ring-text-link focus:ring-offset-2"
          >
            <Avatar initials={userInitials} size="md" />
          </button>

          {/* Profile Menu - Rendered via Portal to escape overflow:hidden */}
          {isProfileMenuOpen &&
            createPortal(
              <div
                ref={profileMenuRef}
                id="sidebar-profile-menu"
                role="menu"
                aria-label="User profile menu"
                className={cn(
                  "fixed z-[9999]",
                  "w-[244px] overflow-hidden rounded-lg",
                  "border border-stroke-secondary bg-bg-primary",
                  "shadow-[0px_4px_8px_0px_rgba(0,0,0,0.12)]",
                )}
                style={{
                  top: menuPosition.top,
                  left: menuPosition.left,
                }}
              >
                {/* User Info Section */}
                <div className="flex items-center gap-3 px-4 py-3">
                  <Avatar initials={userInitials} size="md" />
                  <div className="flex flex-1 flex-col">
                    <span className="text-base font-bold leading-6 text-text-primary">
                      {userName || userInitials}
                    </span>
                  </div>
                </div>

                {/* Divider */}
                <div className="h-px w-full bg-bg-grey" />

                {/* Version Info Section */}
                {(versionName || versionDate) && (
                  <div className="flex flex-col items-start justify-center px-4 py-3">
                    {versionName && (
                      <span className="text-sm font-bold leading-5 text-text-secondary">
                        Version {versionName}
                      </span>
                    )}
                    {versionDate && (
                      <span className="text-sm font-normal leading-5 text-text-subtle">
                        Last updated on {versionDate}
                      </span>
                    )}
                  </div>
                )}
              </div>,
              document.body,
            )}
        </div>

        {/* Intercom Container - Icon injected dynamically via script */}
        {showIntercom && (
          <div
            id="sidebar-intercom-container"
            className="flex h-12 w-12 items-center justify-center"
            aria-label="Support"
          />
        )}
      </div>
    </nav>
  );
}
Sidebar.displayName = "Sidebar";

export { Sidebar };
